import { DATA_SOURCE_TYPES } from '@/components/maplibre/consts';
import {
  ColumnMetadata,
  LayerBbox,
  LayerStats,
  LayerTypeInfo,
  ReadonlyTilesetDataSourceConfig,
} from '@/components/maplibre/types';
import { APP_SETTINGS } from '@/lib/config';
import { LAYER_GEOMETRY_TYPE, TILESET_FORMAT } from '@/lib/db-constants';
import type { MapLayer } from '@/lib/db-row-types';
import { getLayerStyle } from '@/utils/layer-sources/style-processor';

/**
 * Builds a single readonly tileset data source configuration
 */
async function buildReadonlyTilesetDataSource(
  layerInfo: LayerTypeInfo,
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<ReadonlyTilesetDataSourceConfig | null> {
  if (layerInfo.type !== DATA_SOURCE_TYPES.READONLY_TILESET) {
    return null;
  }

  const { layer } = layerInfo;
  const tileId = layer.current_revision_id || layer.id;
  const tileServerUrl = APP_SETTINGS.EXTERNAL_TILE_LAYER_IDS.has(layer.id)
    ? `${process.env.NEXT_PUBLIC_GEOFORGE_WORKER_URL}/api/tiles/${layer.id}/{z}/{x}/{y}.pbf`
    : layer.tileset_format === TILESET_FORMAT.PMTILES
      ? `${process.env.NEXT_PUBLIC_NEXT_MVT_SERVER_BASE_URL}/${tileId}.pmtiles`
      : `${process.env.NEXT_PUBLIC_NEXT_MVT_SERVER_BASE_URL}/${tileId}/{z}/{x}/{y}.pbf`;
  const detectedTilesetFormat =
    layer.tileset_format ||
    (tileServerUrl.toLowerCase().endsWith('.pmtiles')
      ? TILESET_FORMAT.PMTILES
      : layer.tileset_format);
  const mapLayer = mapLayers.find(ml => ml.layer_id === layer.id);
  const columnMetadata = columnMetadataMap.get(layer.id);

  const style = await getLayerStyle(mapLayer, layer, columnMetadata);

  const config: ReadonlyTilesetDataSourceConfig = {
    id: layer.id,
    name: layer.title || 'Untitled Readonly Layer',
    type: DATA_SOURCE_TYPES.READONLY_TILESET,
    tiles: tileServerUrl,
    sourceLayer: 'default',
    geometryTypes: [layer.geometry_type || LAYER_GEOMETRY_TYPE.POLYGON],
    style,
    visible: true,
    bbox: layer.bbox as LayerBbox,
    minzoom: layer.min_zoom ?? undefined,
    maxzoom: layer.max_zoom ?? undefined,
    stats: layer.stats as unknown as LayerStats,
    columnMetadata,
    tilesetFormat: detectedTilesetFormat ?? undefined,
    revisionId: layer.current_revision_id || undefined,
  };

  return config;
}

/**
 * Builds multiple readonly tileset data sources in parallel
 */
export async function buildReadonlyTilesetDataSources(
  readonlyTilesetLayerInfos: LayerTypeInfo[],
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<ReadonlyTilesetDataSourceConfig[]> {
  const buildPromises = readonlyTilesetLayerInfos.map(layerInfo =>
    buildReadonlyTilesetDataSource(layerInfo, mapLayers, columnMetadataMap)
  );

  const results = await Promise.all(buildPromises);

  return results.filter((config): config is ReadonlyTilesetDataSourceConfig => config !== null);
}
